// LabelWorkflowTableRow.tsx
import React from 'react';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDate } from '../utils/timeUtils';
import Avatar from './Avatar';
import StatusIndicator from './StatusIndicator';
import ApprovalCheckBox from './ApprovalCheckBox';
import ActionButton from './ActionButton';
import { useNavigateWithinProject } from '../utils/navigation';
import { useUser } from '@clerk/clerk-react';
import { ApprovalStatus } from '../types';

interface LabelWorkflowTableRowProps {
    label: any;
    labelThumbnailUrl: string;
    userDetailsMapping: Record<string, any>;
    project: any;
}

// Format time elapsed since timestamp (e.g., "2d", "3h", "5m")
const formatTimeElapsed = (timestamp: number): string => {
    const now = Date.now();
    const elapsed = now - timestamp * 1000; // Convert to milliseconds
    const seconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}mo`;
    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return 'now';
};

const LabelWorkflowTableRow: React.FC<LabelWorkflowTableRowProps> = ({
    label,
    labelThumbnailUrl,
    userDetailsMapping,
    project,
}) => {
    const navigate = useNavigateWithinProject();
    const { user } = useUser();

    const userCanReview = (reviewerId: string) => {
        return user && user.id === reviewerId;
    };

    const getReviewerName = (userId: string) => {
        const reviewer = userDetailsMapping[userId];
        return reviewer
            ? `${reviewer.first_name} ${reviewer.last_name}`
            : 'Unknown';
    };

    const uploaderDetails = userDetailsMapping[label.creation.user_id];
    const uploaderName = uploaderDetails
        ? `${uploaderDetails.first_name} ${uploaderDetails.last_name}`
        : 'Unknown User';
    const uploaderEmail = uploaderDetails
        ? uploaderDetails.email_address
        : 'Unknown Email';

    return (
        <tr
            className="workflow-row"
            onClick={() => navigate(`/labels/${label.id}`)}
        >
            {/* Image Thumbnail */}
            <td className="label-thumbnail-cell">
                {labelThumbnailUrl && (
                    <img src={labelThumbnailUrl} alt={label.name} />
                )}
            </td>

            {/* Version */}
            <td className="version-cell">
                <div className="version-name">{label.name}</div>
                {label.notes && (
                    <div className="label-notes">{label.notes}</div>
                )}
            </td>

            {/* Uploaded By */}
            <td>
                <div className="user-details-container">
                    <Avatar
                        userDetails={uploaderDetails}
                        showTooltip={false}
                        height={32}
                    />
                    <div className="text">
                        <p className="user-name">{uploaderName}</p>
                        <p className="user-email">{uploaderEmail}</p>
                    </div>
                </div>
            </td>

            {/* Date */}
            <td>{formatDate(label.creation.timestamp)}</td>

            {/* AI Review */}
            <td className="review-cell ai-review-cell">
                <div className="review-content">
                    <ApprovalCheckBox 
                        status={label.approval_status as ApprovalStatus} 
                        invert={false}
                    />
                </div>
                {/* <div className="reviewer-info">
                    <span>{formatTimeElapsed(label.creation.timestamp)}</span>
                    <span className="separator"> • </span>
                    <span>AI Review</span>
                </div> */}
                {/* OLD CODE - Can be restored later:
                <StatusIndicator
                    decision={label.approval_status}
                    isBig={true}
                />
                */}
            </td>

            {/* Workflow Stages */}
            {project.workflow.stages.map((stage: any, i: number) => {
                const reviewedStage = label.reviewed_stages.find(
                    (rs: any) => stage.id === rs.id,
                );

                if (!reviewedStage) {
                    return (
                        <td key={i} className="review-cell empty-cell">
                        </td>
                    );
                }

                if (reviewedStage.status === 'Pending') {
                    return (
                        <td key={i} className="review-cell pending-cell">
                            <div className="review-content">
                                {userCanReview(stage.reviewer) ? (
                                    <ActionButton
                                        text="Review"
                                        onClick={() => navigate(`/labels/${label.id}`)}
                                        className="branded review-button"
                                        icon={faArrowRight}
                                    />
                                ) : (
                                    <ApprovalCheckBox />
                                    /* OLD CODE - Can be restored later:
                                    <div className="status-with-reviewer">
                                        <StatusIndicator
                                            decision={reviewedStage.status}
                                            isBig={true}
                                        />
                                        <span className="reviewer-name">
                                            by {getReviewerName(stage.reviewer)}
                                        </span>
                                    </div>
                                    */
                                )}
                            </div>
                        </td>
                    );
                }

                return (
                    <td key={i} className="review-cell completed-cell">
                        <div className="review-content">
                            <ApprovalCheckBox 
                                status={reviewedStage.status as ApprovalStatus} 
                                invert={false}
                            />
                        </div>
                        <div className="reviewer-info">
                            <span>{formatTimeElapsed(reviewedStage.timestamp)}</span>
                            <span className="separator"> • </span>
                            <span>{getReviewerName(reviewedStage.user_id)}</span>
                            {reviewedStage.comment && (
                                <>
                                    <span className="separator"> • </span>
                                    <FontAwesomeIcon icon={faComment} className="comment-icon" />
                                </>
                            )}
                        </div>
                        {/* OLD CODE - Can be restored later:
                        <div className="status-with-reviewer">
                            <StatusIndicator
                                decision={reviewedStage.status}
                                isBig={true}
                            />
                            <span className="reviewer-name">
                                by {getReviewerName(reviewedStage.user_id)}
                            </span>
                        </div>
                        {reviewedStage.comment && (
                            <div className="comment-bubble">
                                <span>{reviewedStage.comment}</span>
                            </div>
                        )}
                        */}
                    </td>
                );
            })}
        </tr>
    );
};

export default LabelWorkflowTableRow;
